import {
  Component,
  EventEmitter,
  Output,
  inject,
  OnInit,
  signal,
} from '@angular/core';

import { Types } from '../../../../../domain/types.domain';
import { IconComponent, LoadingSpinnerComponent, FileUploadComponent, CheckboxComponent } from '../../../../../@ads/components';
import { CreateAccountPjFormHandler } from '../../create-account-pj-form.handler';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

export type RfValidationStatus =
  | 'loading'
  | 'success'
  | 'document-required-intro'
  | 'document-required'
  | 'done';

@Component({
  selector: 'app-rf-validation',
  standalone: true,
  imports: [
    IconComponent,
    LoadingSpinnerComponent,
    FileUploadComponent,
    CheckboxComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './rf-validation.component.html',
  styleUrl: './rf-validation.component.scss',
})
export class RfValidationComponent implements OnInit {
  private readonly _handler = inject(CreateAccountPjFormHandler);
  private readonly _validateRfService = inject(Types.ValidateRfService);
  private readonly _uploadDocumentService = inject(Types.UploadDocumentService);

  @Output() setStepValidation = new EventEmitter<boolean>();
  @Output() requestBackToReview = new EventEmitter<void>();
  @Output() introStateChange = new EventEmitter<boolean>();

  public status = signal<RfValidationStatus>('loading');
  public errorMessage = signal<string | null>(null);

  public termAccepted = new FormControl(false, {
    nonNullable: true,
    validators: [Validators.requiredTrue],
  });
  public uploadedFiles = signal<Map<string, File>>(new Map());
  public isUploading = signal(false);

  get requiresDocument(): boolean {
    return this.status() === 'document-required';
  }

  get isInIntroState(): boolean {
    return this.status() === 'document-required-intro';
  }

  get representatives() {
    return this._handler.getRepresentatives();
  }

  private readonly RELACAO_LABELS: Record<string, string> = {
    'socio-proprietario': 'Sócio-proprietário',
    diretor: 'Diretor',
    procurador: 'Procurador',
    proprietario: 'Proprietário',
    presidente: 'Presidente',
    outra: 'Outra',
  };

  getRelacaoLabel(value: string | undefined): string {
    if (!value) return '';
    return this.RELACAO_LABELS[value] ?? value;
  }

  formatCpf(cpf: string): string {
    const digits = (cpf ?? '').replace(/\D/g, '');
    if (digits.length !== 11) return cpf ?? '';
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  get canProceedWithDocument(): boolean {
    if (this.status() !== 'document-required') return false;
    const reps = this.representatives;
    if (!reps.length) return false;
    const failedReps = reps.filter((rep) => !this.getRfResult(rep.cpf)?.valid);
    if (!failedReps.length) return true;
    return failedReps.every((rep) => this.uploadedFiles().has(rep.cpf));
  }

  getRfResult(cpf: string) {
    return this._handler.getRfResults().get(cpf);
  }

  getUploadedFile(cpf: string): File | null {
    return this.uploadedFiles().get(cpf) ?? null;
  }

  ngOnInit(): void {
    this.startValidation();
  }

  async startValidation(): Promise<void> {
    this.status.set('loading');
    this.setStepValidation.emit(false);

    const representatives = this._handler.getRepresentatives();
    const cnpj = this._handler.getCompanyInfo()?.cnpj ?? '';

    if (!representatives.length || !cnpj) {
      this.status.set('document-required-intro');
      this.introStateChange.emit(true);
      return;
    }

    try {
      let allValid = true;

      for (const rep of representatives) {
        let timeoutId: ReturnType<typeof setTimeout>;
        const result = await Promise.race([
          this._validateRfService.handle({ cpf: rep.cpf, cnpj }),
          new Promise<never>((_, reject) => {
            timeoutId = setTimeout(() => reject(new Error('timeout')), 10000);
          }),
        ]).finally(() => clearTimeout(timeoutId));

        this._handler.setRfResult(rep.cpf, {
          cpf: rep.cpf,
          valid: result.valid,
          relacao: result.relacao,
          motivo: result.motivo,
        });

        if (!result.valid) {
          allValid = false;
        }
      }

      if (allValid) {
        this.status.set('success');
        this.setStepValidation.emit(true);
      } else {
        this.status.set('document-required-intro');
        this.introStateChange.emit(true);
      }
    } catch {
      this.status.set('document-required-intro');
      this.introStateChange.emit(true);
    }
  }

  goToDocumentUpload(): void {
    this.status.set('document-required');
    this.introStateChange.emit(false);
    this.setStepValidation.emit(this.canProceedWithDocument);
  }

  onBackToReview(): void {
    this.requestBackToReview.emit();
  }

  onFileSelected(cpf: string, file: File): void {
    const map = new Map(this.uploadedFiles());
    map.set(cpf, file);
    this.uploadedFiles.set(map);
    this.checkDocumentComplete();
  }

  onFileRemoved(cpf: string): void {
    const map = new Map(this.uploadedFiles());
    map.delete(cpf);
    this.uploadedFiles.set(map);
    this.checkDocumentComplete();
  }

  onTermChanged(): void {
    this.checkDocumentComplete();
  }

  private checkDocumentComplete(): void {
    if (this.canProceedWithDocument) {
      this.setStepValidation.emit(true);
    } else {
      this.setStepValidation.emit(false);
    }
  }

  async uploadDocumentForAll(): Promise<boolean> {
    const cnpj = this._handler.getCompanyInfo()?.cnpj ?? '';
    const termAcceptedAt = new Date().toISOString();

    this.isUploading.set(true);

    try {
      for (const rep of this._handler.getRepresentatives()) {
        const file = this.uploadedFiles().get(rep.cpf);
        if (!file) continue;

        const result = await this._uploadDocumentService.handle({
          file,
          cnpj,
          cpf: rep.cpf,
          termAcceptedAt,
        });

        this._handler.setRfResult(rep.cpf, {
          cpf: rep.cpf,
          valid: true,
          documentUrl: result.documentUrl,
          termAcceptedAt,
        });
      }

      this.status.set('done');
      return true;
    } catch (err: unknown) {
      const e = err as { error?: { message?: string } | string; message?: string };
      const msg =
        typeof e?.error === 'object' && typeof e.error?.message === 'string'
          ? e.error.message
          : typeof e?.error === 'string'
            ? e.error
            : typeof e?.message === 'string'
              ? e.message
              : null;
      this.errorMessage.set(
        msg ?? 'Não foi possível enviar o documento. Tente novamente.'
      );
      return false;
    } finally {
      this.isUploading.set(false);
    }
  }
}
